import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, take, tap } from 'rxjs/operators';
import { EndPointService } from 'src/app/core/services/end-point.service';
import { PagingState } from 'src/app/shared/framework/paging/PagingState';

@Component({
  selector: 'app-basic-select-modal',
  templateUrl: './basic-select-modal.component.html',
  styleUrls: ['./basic-select-modal.component.scss'],
})
export class BasicSelectModalComponent implements OnInit, OnDestroy {
  @Input() modalTitle: string;
  @Input() idProperty: string;
  @Input() displayProperties: {
    property: string;
    display: string;
    classes?: string[];
  }[];
  @Input() multiSelect: boolean;
  @Input() extraEndpointData: {};

  @Input() endPointId: number;
  @Input() itemEndPoint: string;
  @Input() countEndPoint: string;
  @Input() selectedList: any[];
  @Input() allItems: any[];
  @Output() confirm: EventEmitter<any[]> = new EventEmitter<any[]>();

  public viewList: any[];
  public internalSelectedList: any[] = [];
  public count = 0;

  public searchForm: FormGroup;
  private _searchSubscription$: Subscription;
  public pagingState: PagingState = new PagingState();

  public constructor(
    public modal: NgbActiveModal,
    private _endPointService: EndPointService,
    private _formBuilder: FormBuilder
  ) { }

  public ngOnInit(): void {
    this.internalSelectedList = [...this.selectedList].filter((e) => e);
    this.setupSearchForm();
    this.setCount();
    this.setViewList();
  }

  public ngOnDestroy(): void {
    this._searchSubscription$.unsubscribe();
  }

  private setupSearchForm(): void {
    this.searchForm = this._formBuilder.group({
      search: '',
    });
    this._searchSubscription$ = this.searchForm.controls['search'].valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        tap((search: string) => {
          this.pagingState.search = search;
          this.setCount();
          this.setViewList();
        })
      )
      .subscribe();
  }

  private setCount(): void {
    if (this.allItems) {

      if (this.pagingState.search) {
        let counter = 0;
        for (const item of this.allItems) {
          for (const property of this.displayProperties) {
            const value = this.getDisplayValue(
              item,
              property.property,
              property.property.split('.')
            );
            if (
              value &&
              value.toString().toLowerCase().includes(this.pagingState.search.toLowerCase())
            ) {
              counter++;
              break; // No need to check other properties for this item
            }
          }
        }
        this.count = counter;
      } else {
        this.count = this.allItems.length;
      }

      return;
    }

    let data: any = {
      searchString: this.pagingState.search,
      id: this.endPointId ? this.endPointId : null,
    };
    if (!this.endPointId) {
      data = { searchString: this.pagingState.search };
    }

    data = { ...data, ...this.extraEndpointData };

    this._endPointService
      .call<number>(this.countEndPoint, data)
      .pipe(take(1))
      .subscribe((count: number) => {
        this.count = count;
      });
  }

  private setViewList(): void {
    let data: any = {
      page: this.pagingState.page - 1,
      pageSize: this.pagingState.pageSize,
      search: this.pagingState.search,
    };

    if (this.allItems) {
      if (this.pagingState.search) {
        let items = [];
        for (const item of this.allItems) {
          for (const property of this.displayProperties) {
            const value = this.getDisplayValue(
              item,
              property.property,
              property.property.split('.')
            );
            if (
              value &&
              value.toString().toLowerCase().includes(this.pagingState.search.toLowerCase())
            ) {
              items.push({ ...item, selected: false });
              break; // No need to check other properties for this item
            }
          }
        }
        this.viewList = items;

      } else {
        this.viewList = this.allItems.map((item: any) => {
          return { ...item, selected: false };
        });
      }

      this.viewList = this.viewList.slice(
        (this.pagingState.page - 1) * this.pagingState.pageSize,
        (this.pagingState.page) * this.pagingState.pageSize
      );

      return;
    }

    if (this.endPointId) {
      data = {
        id: this.endPointId,
        pagingState: {
          page: this.pagingState.page - 1,
          pageSize: this.pagingState.pageSize,
          search: this.pagingState.search,
        },
      };
    }

    data = { ...data, ...this.extraEndpointData };

    this._endPointService
      .call<any[]>(this.itemEndPoint, data)
      .pipe(
        take(1),
        tap((items: any[]) => {
          this.viewList = items.map((item: any) => {
            return { ...item, selected: false };
          });
          this.evaluateSelected();
        })
      )
      .subscribe();
  }

  private evaluateSelected(): void {
    this.viewList.forEach((item: any) => {
      const itemWithoutSelected: any = { ...item };
      delete itemWithoutSelected['selected'];
      if (!item.selected) {
        item.selected =
          this.internalSelectedList.filter((si: any) => {
            return si[this.idProperty] === item[this.idProperty];
          }).length > 0;
      }
    });
  }

  public selectItem(index: number): void {
    this.viewList[index].selected = !this.viewList[index].selected;
    if (this.viewList[index].selected) {
      const itemWithoutSelected: any = { ...this.viewList[index] };
      delete itemWithoutSelected['selected'];
      if (this.multiSelect) {
        this.internalSelectedList.push(itemWithoutSelected);
      } else {
        this.internalSelectedList = [itemWithoutSelected];
        this.confirmSelection();
      }
    } else {
      if (this.multiSelect) {
        this.internalSelectedList = this.internalSelectedList.filter(
          (item: any) => {
            return (
              item[this.idProperty] !== this.viewList[index][this.idProperty]
            );
          }
        );
      }
    }
  }

  public confirmSelection(): void {
    this.confirm.emit(this.internalSelectedList);
  }

  public changePage(newPage: number): void {
    this.pagingState.page = newPage;
    this.setViewList();
  }

  public getDisplayValue(
    item: any,
    property: string,
    propertyPath?: string[],
    index?: number
  ): any {
    if (item == null) {
      return null;
    }
    if (!propertyPath) {
      propertyPath = property.split('.');
      index = 0;
      property = propertyPath[index];
    }
    if (index < propertyPath.length - 1) {
      const newItem: any = item[property];
      return this.getDisplayValue(
        newItem,
        propertyPath[++index],
        propertyPath,
        index
      );
    } else {
      return item[property];
    }
  }
}
